import Vue from 'vue';
import VueRouter from 'vue-router';
import * as Sentry from '@sentry/browser';
import i18n from '@/i18n';
import socket from '@/socket';
import store from '@/store';
import { LOGIN, SET_LOGIN_ERRORS, SET_COMMON_ERRORS } from '@/store/modules/user/types';
import { SET_RECENT_CHATS, SET_ACTIVE_CHAT_ID } from '@/store/modules/chats/types';
import { UPDATE_ACTIVITY_STATUS } from '@/store/modules/users/types';

import CONSTANTS from '@/constants';

Vue.use(VueRouter);

const loginAndGetRecentChats = data => {
  store.commit(`user/${LOGIN}`, data);

  Sentry.configureScope(scope => scope.setUser({
    ...data,
    username: data.slug
  }));

  const cb = response => {
    const privateChats = response.chats.filter(chat => !!chat.contact);

    if (privateChats.length) {
      privateChats.forEach(chat => {
        store.commit(`users/${UPDATE_ACTIVITY_STATUS}`, { user_id: chat.contact.id, active_at: chat.contact.active_at });
      });
    }

    store.commit(`chats/${SET_RECENT_CHATS}`, response);
  };

  socket.emit('recent-chats', { from: 0, per_page: CONSTANTS.chatsPageSize }, cb);
};

const handleSignInErrors = errors => {
  socket.disconnect();
  store.commit(`user/${SET_LOGIN_ERRORS}`, errors);
};

const signin = next => {
  if (store.state.user.loggedIn) {
    next();
    return;
  }

  socket.signin(data => {
    loginAndGetRecentChats(data);
    next();
  }, errors => {
    handleSignInErrors(errors);
    next();
  });
};

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/Home'),
    meta: { title: `${i18n.t('pageTitles.home')} - Kick Messenger` }
  },
  {
    path: '/chat/:chatId(\\d+)',
    name: 'chat-by-id',
    meta: { title: 'Kick Messenger' },
    beforeEnter: (to, from, next) => {
      const { params: { chatId } } = to;

      if (localStorage.getItem('access_token')) {
        store.commit(`chats/${SET_ACTIVE_CHAT_ID}`, +chatId);
        next('/');
      } else {
        window.location = `${process.env.VUE_APP_AUTH}?signin`;
      }
    }
  },
  {
    path: '/chat/:userSlug',
    name: 'chatWithReferral',
    meta: { title: `${i18n.t('pageTitles.chatWithReferral')} - Kick Messenger` },
    beforeEnter: (to, from, next) => {
      const { params: { userSlug } } = to;

      socket.emit('message-referral', { nickname: userSlug }, response => {
        if (response.errors) {
          store.commit(`user/${SET_COMMON_ERRORS}`, response.errors);
          setTimeout(() => store.commit(`user/${SET_COMMON_ERRORS}`, []), 3000);
        }
      });

      next({ path: '/', replace: true });
    }
  },
  {
    path: '*',
    name: 'not found',
    component: () => import('@/views/Page404'),
    meta: { title: `${i18n.t('pageTitles.pageNotFound')} - Kick Messenger` }
  }
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
});

router.beforeEach((to, from, next) => {
  const { accessToken, refreshToken, session } = to.query;

  if (accessToken && refreshToken && session) {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('session_group_id', session);

    if (process.env.NODE_ENV !== 'production') {
      console.log(`%c${accessToken}`, 'color: #fff; font-weight: bold; background: rgb(50, 150, 220); padding:3px 5px;');
      console.log(`%c${refreshToken}`, 'color: #fff; font-weight: bold; background: rgb(50, 150, 220); padding:3px 5px;');
    }

    next({ path: to.path, query: {}, replace: true });
    return;
  }

  // Inform log
  if (process.env.NODE_ENV !== 'production') {
    console.log(`%cGoTo: ${document.title} (${to.path})`, 'color: #fff; font-weight: bold; background: rgb(70,100,100); padding:3px 5px;');
  }

  // Update title
  document.title = to.meta.title ? to.meta.title : 'Kick Messenger';

  // Get/Update User data
  if (!localStorage.getItem('access_token') && !localStorage.getItem('refresh_token')) {
    window.location = process.env.VUE_APP_AUTH;
  } else if (!socket.connected) {
    socket.connect();

    Vue.prototype.$eventHub.$on('socketConnected', () => signin(next));
  } else {
    signin(next);
  }
});

export default router;
